/**
 * Pure helpers behind the "Jump to column" picker (JumpToColumn.tsx). Kept free
 * of React and MUI so the option building and matching can be unit tested.
 */

/** The parts of a grid column definition the picker needs. */
export interface JumpToColumnSourceColumn {
  field: string;
  headerName?: string;
  type?: string;
}

export interface JumpToColumnOption {
  field: string;
  label: string;
  hidden: boolean;
  pinned: boolean;
}

// MUI X internal columns that are never useful jump targets, even though MUI
// gives them header names: checkbox selection, detail panel toggle (always
// hidden by ServerDataGrid) and the row reorder handle.
const NEVER_JUMPABLE_FIELDS = new Set([
  "__check__",
  "__detail_panel_toggle__",
  "__reorder__",
]);

// MUI X names its generated columns "__name__" (row grouping, tree data)
const isInternalField = (field: string) => /^__.+__$/.test(field);

/**
 * True for columns a user would recognize as data: MUI X's own utility columns
 * are left out, and internal or actions columns count only when they carry a
 * real headerName. Shared with Advanced Copy's column list.
 */
export function isUserFacingColumn(col: JumpToColumnSourceColumn): boolean {
  if (NEVER_JUMPABLE_FIELDS.has(col.field)) return false;
  const needsHeaderName = isInternalField(col.field) || col.type === "actions";
  return !needsHeaderName || !!col.headerName?.trim();
}

const normalizeText = (text: string) =>
  text.trim().toLowerCase().replace(/\s+/g, " ");

// "amazon_listings.listing_price" -> "amazon listings listing price"
const splitIntoWords = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Builds the picker options from the grid's columns, in grid order. Hidden
 * columns are kept (and flagged) so the picker can reveal them. Internal and
 * unnamed actions columns are skipped unless they carry a real headerName.
 */
export function buildJumpToColumnOptions(
  columns: JumpToColumnSourceColumn[],
  columnVisibilityModel: Record<string, boolean | undefined> = {},
  pinnedFields: Iterable<string> = []
): JumpToColumnOption[] {
  const pinned = new Set(pinnedFields);
  const options: JumpToColumnOption[] = [];

  for (const col of columns) {
    if (!isUserFacingColumn(col)) continue;

    const headerName = col.headerName?.trim() ?? "";
    options.push({
      field: col.field,
      // Same fallback the grid uses when a column has no headerName
      label: headerName || col.field,
      hidden: columnVisibilityModel[col.field] === false,
      pinned: pinned.has(col.field),
    });
  }

  return options;
}

/**
 * Lower is a better match; null means no match. Ranks, best first: exact
 * label/field, label prefix, label word prefix, label substring, field
 * substring, then every query word somewhere in the label or field.
 */
function matchRank(
  option: JumpToColumnOption,
  query: string,
  queryWords: string,
  tokens: string[]
): number | null {
  const label = normalizeText(option.label);
  const field = option.field.toLowerCase();
  const fieldWords = splitIntoWords(option.field);

  if (label === query || field === query) return 0;
  if (label.startsWith(query)) return 1;
  if (queryWords && ` ${splitIntoWords(label)}`.includes(` ${queryWords}`)) {
    return 2;
  }
  if (label.includes(query)) return 3;
  if (
    field.includes(query) ||
    (queryWords && fieldWords.includes(queryWords))
  ) {
    return 4;
  }

  const haystack = `${label} ${field} ${fieldWords}`;
  if (tokens.length > 1 && tokens.every((token) => haystack.includes(token))) {
    return 5;
  }
  return null;
}

/**
 * Case-insensitive substring match on headerName and field, best matches
 * first (so Enter on the top option does the obvious thing) and grid order
 * within the same rank. An empty query returns every option unchanged.
 */
export function filterJumpToColumnOptions(
  options: JumpToColumnOption[],
  query: string
): JumpToColumnOption[] {
  const normalized = normalizeText(query);
  if (!normalized) return options;

  const queryWords = splitIntoWords(normalized);
  const tokens = normalized.split(" ").filter(Boolean);

  const matches: { option: JumpToColumnOption; index: number; rank: number }[] =
    [];
  options.forEach((option, index) => {
    const rank = matchRank(option, normalized, queryWords, tokens);
    if (rank !== null) matches.push({ option, index, rank });
  });

  return matches
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((match) => match.option);
}
